import { useState } from "react";
import { mediaUrl } from "@/lib/media";
import { cn } from "@/lib/utils";

export interface CurrentUserDisplay {
  name: string;
  email: string;
  initials: string;
  avatarUrl: string | null;
}

/** Display fields for the logged-in user, read from the stored login payload. */
export function getCurrentUserDisplay(): CurrentUserDisplay {
  let user = null;
  try {
    const userRaw = localStorage.getItem("user");
    user = userRaw ? JSON.parse(userRaw) : null;
  } catch {
    // Corrupt / unavailable storage — fall back to defaults below
  }

  // `UserProfile.name` is the canonical display name on the backend (it's
  // the field shown in the admin profile editor). Prefer it as-is and only
  // fall through to the component parts if it's blank — that way edits to
  // `name` always render exactly as typed (e.g. "Asif Altaf - IP Owner")
  // instead of being silently overridden by a composed first/last.
  const email = user?.email || "admin@tudonum.com";
  const composedFullName = [user?.first_name, user?.last_name]
    .filter((s) => typeof s === "string" && s.trim().length > 0)
    .join(" ")
    .trim();
  const name =
    (typeof user?.name === "string" && user.name.trim()) ||
    composedFullName ||
    user?.full_name ||
    user?.username ||
    email.split("@")[0] ||
    "Admin User";

  const initials = name
    .split(" ")
    .map((n: string) => n[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();

  // Profile picture is resolved through the same MEDIA_URL helper used
  // everywhere else (services, providers, restaurants, stores, companies).
  // The login response may put the path under either `profile_picture`
  // (LoginUserSchema) or a nested `profile.profile_picture`; check both.
  const rawAvatar = user?.profile_picture ?? user?.profile?.profile_picture ?? null;

  return { name, email, initials, avatarUrl: mediaUrl(rawAvatar) };
}

export function UserAvatar({
  user,
  className,
}: {
  user: CurrentUserDisplay;
  className?: string;
}) {
  const [avatarFailed, setAvatarFailed] = useState(false);
  const showAvatar = Boolean(user.avatarUrl) && !avatarFailed;

  return showAvatar ? (
    <img
      src={user.avatarUrl as string}
      alt={user.name}
      className={cn("h-9 w-9 shrink-0 rounded-full object-cover bg-muted", className)}
      // If the URL 404s (file moved, MEDIA_URL misconfigured, etc.) we
      // gracefully degrade to the initials avatar instead of showing a
      // broken-image icon.
      onError={() => setAvatarFailed(true)}
    />
  ) : (
    <div
      className={cn(
        "flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground",
        className
      )}
    >
      {user.initials}
    </div>
  );
}

export default function UserItem({ compact = false }: { compact?: boolean }) {
  const user = getCurrentUserDisplay();

  return (
    <div
      className={
        compact
          ? "flex justify-center py-1"
          : "flex items-center gap-3 rounded-lg bg-sidebar-accent/50 px-3 py-2.5"
      }
      title={compact ? `${user.name} · ${user.email}` : undefined}
    >
      <UserAvatar user={user} />

      {!compact && (
        <div className="flex min-w-0 flex-col">
          <p className="truncate text-sm font-medium text-foreground">{user.name}</p>
          <p className="truncate text-[11px] text-muted-foreground">{user.email}</p>
        </div>
      )}
    </div>
  );
}
